import { DetailedHTMLProps, HTMLAttributes, MouseEvent, forwardRef } from 'react'
import { IconNames } from './Icon.type'
import classNames from 'classnames'

interface IconProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement> {
  name?: IconNames
  size?: number | string
  color?: string
  className?: string
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void
  ref?: any
  /**
   * Accessibility: Provide descriptive label for screen readers
   * Use when icon conveys meaning (not purely decorative)
   * Requirements: 12.1, 12.5 - Screen reader support
   */
  'aria-label'?: string
  /**
   * Accessibility: Hide decorative icons from screen readers
   * Use when icon is purely decorative or has adjacent text
   * Requirements: 12.1, 12.5 - Screen reader support
   */
  'aria-hidden'?: boolean | 'true' | 'false'
}

const Icon = (
  { 
    name, 
    size = 24, 
    color, 
    className, 
    style, 
    'aria-label': ariaLabel,
    'aria-hidden': ariaHidden,
    ...rest 
  }: IconProps,
  ref: any
) => {
  // Determine aria-hidden value
  // If aria-label is provided, icon is meaningful and should not be hidden
  // If aria-hidden is explicitly set, use that value
  // Otherwise, default to hiding decorative icons
  const shouldHide = ariaHidden !== undefined 
    ? ariaHidden 
    : !ariaLabel;

  return (
    <span
      ref={ref}
      className={classNames(`icon-${name}`, className)}
      style={{ fontSize: size, color, ...style }}
      aria-label={ariaLabel}
      aria-hidden={shouldHide}
      role={ariaLabel ? 'img' : undefined}
      {...rest}
    />
  )
}

export default forwardRef<any, IconProps>(Icon)
